<template>
    <app-layout title="User Management">
        <template #header>
            <div class="flex justify-between">
                <h2 class="font-semibold text-xl text-gray-800 dark:text-gray-200 leading-tight">
                    User Management
                </h2>
                <div class="flex">
                    <inertia-link :href="route('admin.user.invite.form')"
                                  class="mr-2 max-w-min whitespace-nowrap flex items-center justify-between w-full px-4 py-2 text-sm font-medium leading-5 text-white transition-colors duration-150 bg-red-600 border border-transparent rounded-lg active:bg-red-600 hover:bg-red-700 focus:outline-none focus:shadow-outline-red">
                        Invite user
                        <span class="ml-2" aria-hidden="true">+</span>
                    </inertia-link>
                    <inertia-link :href="route('admin.user.create')"
                                  class="max-w-min whitespace-nowrap flex items-center justify-between w-full px-4 py-2 text-sm font-medium leading-5 text-white transition-colors duration-150 bg-red-600 border border-transparent rounded-lg active:bg-red-600 hover:bg-red-700 focus:outline-none focus:shadow-outline-red">
                        Create user
                        <span class="ml-2" aria-hidden="true">+</span>
                    </inertia-link>
                </div>
            </div>
        </template>
        <div class="w-full overflow-hidden rounded-lg shadow">
            <table-layout>
                <template v-slot:theadRow>
                    <th class="px-4 py-3">Name</th>
                    <th class="px-4 py-3">Email</th>
                    <th class="px-4 py-3">Roles</th>
                    <th class="px-4 py-3">Created at</th>
                    <th class="px-4 py-3">Status</th>
                    <th class="px-4 py-3">Actions</th>
                </template>
                <template v-slot:tbody>
                    <tr v-for="user in paginatedUsers.data" class="text-gray-700 dark:text-gray-400">
                        <td class="px-4 py-3">
                            <div class="flex items-center text-sm">
                                <div
                                    class="relative hidden w-8 h-8 mr-3 rounded-full md:block"
                                >
                                    <img
                                        class="object-cover w-full h-full rounded-full"
                                        :src="user.profile_photo_url"
                                        alt=""
                                        loading="lazy"
                                    />
                                    <div
                                        class="absolute inset-0 rounded-full shadow-inner"
                                        aria-hidden="true"
                                    ></div>
                                </div>
                                <div>
                                    <p class="font-semibold">{{ user.name || user.email }}</p>
                                </div>
                            </div>
                        </td>
                        <td class="px-4 py-3 text-sm">
                            {{ user.email }}
                        </td>
                        <td>
                            <template v-for="role in getUniqueUserRoles(user.roles)">
                                <p class="font-semibold">{{ role.name }}</p>
                                <p v-if="role.pivot?.tenant_id" class="text-xs text-gray-600 dark:text-gray-400">
                                    {{clients[role.pivot?.tenant_id]?.name}}
                                </p>
                            </template>
                        </td>
                        <td class="px-4 py-3 text-sm">
                            {{ dayjs(user.created_at).format("DD-MM-YYYY HH:mm") }}
                        </td>
                        <td class="px-4 py-3 text-xs">
                            <pill :type="user.email_verified_at && user.active ? 'success'
                                            : (user.onboarding_level ? 'danger' : 'warning')">
                                {{ user.email_verified_at
                                    ? (
                                        user.active ? 'Email verified' : 'Disabled'
                                    )
                                    : (
                                        !user.onboarding_level ? 'Invited' : 'Pending email verification'
                                    ) }}
                            </pill>
                        </td>
                        <td class="px-4 py-3">
                            <div class="flex items-center space-x-4 text-sm">
                                <inertia-link :href="route('admin.user.edit', user.id)"
                                              class="flex items-center justify-between px-2 py-2 text-sm font-medium leading-5 text-gray-600 rounded-lg dark:text-gray-400 focus:outline-none focus:shadow-outline-gray"
                                              aria-label="Edit">
                                    <svg class="w-5 h-5" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                                        <path
                                            d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"></path>
                                    </svg>
                                </inertia-link>
                                <button @click.prevent="deleteUser(user)"
                                        class="flex items-center justify-between px-2 py-2 text-sm font-medium leading-5 text-gray-600 rounded-lg dark:text-gray-400 focus:outline-none focus:shadow-outline-gray"
                                        aria-label="Delete"
                                >
                                    <svg class="w-5 h-5" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd"
                                              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                              clip-rule="evenodd"></path>
                                    </svg>
                                </button>
                            </div>
                        </td>
                    </tr>
                </template>
            </table-layout>
            <table-pagination :pagination-object="paginatedUsers"/>
        </div>
    </app-layout>
</template>

<script>
import {defineComponent} from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import TablePagination from "@/Jetstream/TablePagination";
import TableLayout from "@/Jetstream/TableLayout";
import Pill from "@/Jetstream/Pill";
import {Inertia} from '@inertiajs/inertia'
import {inject} from "vue";
import dayjs from "dayjs";

export default defineComponent({
    computed: {
        dayjs() {
            return dayjs
        }
    },
    components: {
        Pill,
        TableLayout,
        TablePagination,
        AppLayout
    },
    props: {
        paginatedUsers: Object,
        clients: Object,
    },
    data() {
        return {
            title: "Test"
        }
    },
    setup() {
        const $swal = inject('$swal');
        const deleteUser = (user) => {
            $swal({
                title: 'Are you sure you want to delete this user?',
                text: "You won't be able to revert this!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, delete it!'
            }).then((result) => {
                if (result.isConfirmed) {
                    Inertia.delete(route('admin.user.destroy', user.id));
                }
            });
        }
        const getUniqueUserRoles = (userRoles) => {
            const result = [];
            userRoles.forEach(role => {
                if (!result.filter(
                    addedRole => addedRole.id === role.id
                        && addedRole.pivot.tenant_id === role.pivot.tenant_id
                ).length) {
                    result.push(role)
                }
            })
            return result;
        }
        return {
            deleteUser,
            getUniqueUserRoles
        };
    }
})
</script>
